import { Images } from '@/assets/images';
import { DocTypeEnum, ExtensionStayEnum, REGEX } from '@/constant';
import {
	cn,
	convertNationCodeToName,
	convertProductionTypeCodeToName,
	dayjs,
	formatDate,
	insertObjectIfV2,
} from '@/utils';

import { range } from 'ramda';
import { isNotNilOrEmpty } from 'ramda-adjunct';
import * as Yup from 'yup';

import { CheckboxType, FormConfig } from '../define-document-write.type';

export enum FactType {
	NON_PAYMENT = 'non_payment',
	INSURANCE = 'insurance',
	NO_CHANGES = 'no_changes',
}

export enum AgricultureReasonType {
	SOWING = 'sowing',
	SEEDLINGS = 'seedlings',
	WEEDING = 'weeding',
	HARVESTING = 'harvesting',
}

export enum FishingReasonType {
	PROCESS = 'process',
	PRODUCTION = 'production',
}

const initializeValue = {
	registrationNo: '',
	receiptDate: '',
	processingEndDate: '',
	recruiterName: '',
	franchiseName: '',
	franchisePhone: '',
	franchiseAddress: '',
	businessRegisNo: '',
	franchiseType: '',
	franchiseSignature: '',
	factStatement: Object.values(FactType) as string[],
	reasonsAgriculture: [AgricultureReasonType.SOWING] as string[],
	reasonsFishing: [] as string[],
	period: [ExtensionStayEnum.THREE_MONTHS],
	recommendationExtensionOfStayInfoJson: range(0, 5).map((item) => ({
		id: item,
		appcSerialNo: String(item + 1),
		appcName: '',
		appcAlienRegistNo: '',
		appcNational: '',
		appcPassportNo: '',
		appcStayPeriod: '',
		appcSignature: '',
	})),
	recomExtOfStayRegistDate: dayjs().format('YYYY-MM-DD'),
	recomExtOfStayEmployer: '',
	recomExtOfStaySignature: '',
	dearToText: '',
};

type IValues = typeof initializeValue;

export const RENEW_STAY_APPLICATION_CONFIG: FormConfig<
	IValues,
	DocTypeEnum.ES1_09
> = {
	initializeValue,
	title: '체류기간 연장 추천 신청서',
	mapPropToValues: ({
		franchiseData,
		detailData,
		unitData,
		applicantListData,
	}) => {
		const extensionPeriods = [
			...new Set(
				applicantListData
					?.map((item) => item?.extensionPeriod)
					.filter(Boolean) as ExtensionStayEnum[]
			),
		];

		return {
			recruiterName: franchiseData?.employerName || '',
			franchisePhone: franchiseData?.employerPhone || '',
			franchiseName: franchiseData?.name || '',

			franchiseAddress: franchiseData?.address || '',
			businessRegisNo: franchiseData?.employerBirthDate
				? `${formatDate(franchiseData?.employerBirthDate, {
						outputFormat: 'YYMMDD',
					})}-`
				: '',
			franchiseType: `${convertProductionTypeCodeToName(franchiseData?.productionType) || ''}${franchiseData?.productionTypeDetail ? `(${franchiseData?.productionTypeDetail})` : ''}`,
			recommendationExtensionOfStayInfoJson: range(0, 5).map((item, index) => ({
				id: item,
				appcSerialNo: String(item + 1),
				appcName: applicantListData?.[item]?.fullName || '',
				appcAlienRegistNo: applicantListData?.[item]?.foreignerNo || '',
				appcNational: convertNationCodeToName(
					applicantListData?.[item]?.nationalCode || ''
				),
				appcPassportNo: applicantListData?.[item]?.passportNo || '',
				appcStayPeriod: applicantListData?.[item]?.departureDate || '',
				appcSignature: applicantListData?.[item]?.fullName || '',
			})),
			dearToText: unitData?.name
				? unitData?.name
				: detailData?.unitInfo?.name || '',
			recomExtOfStayEmployer: franchiseData?.employerName || '',
			...insertObjectIfV2(extensionPeriods?.length > 0, {
				period: extensionPeriods,
			}),
		};
	},
	pages: [
		{
			background: {
				src: Images.renewStayApplication,
				className: 'template',
				dataSource: Images.renewStayApplication,
			},
			textAreas: [
				{
					name: 'registrationNo',
					maxHeight: 24,
					maxLength: 20,
					containerClassName: 'absolute top-[165px] left-[145px] w-[208px]',
					regex: REGEX.ALLOW_NUMBER_SPECIAL_CHARS,
				},
				{
					name: 'recruiterName',
					maxHeight: 24,
					maxLength: 20,
					containerClassName: 'absolute top-[222px] left-[83px] w-[367px]',
					className: 'text-[17.33px] leading-none',
				},
				{
					name: 'franchisePhone',
					maxHeight: 24,
					maxLength: 15,
					containerClassName: 'absolute top-[222px] left-[459px] w-[253px]',
				},
				{
					name: 'franchiseName',
					maxHeight: 24,
					maxLength: 40,
					containerClassName: 'absolute top-[264px] left-[83px] w-[367px]',
				},
				{
					name: 'franchiseAddress',
					maxHeight: 24,
					maxLength: 40,
					containerClassName: 'absolute top-[264px] left-[459px] w-[254px]',
				},
				{
					name: 'businessRegisNo',
					maxHeight: 24,
					maxLength: 20,
					containerClassName: 'absolute top-[318px] left-[83px] w-[367px]',
					regex: REGEX.ALLOW_NUMBER_SPECIAL_CHARS,
				},
				{
					name: 'franchiseType',
					maxHeight: 24,
					maxLength: 30,
					containerClassName: 'absolute top-[318px] left-[459px] w-[253px]',
				},
				(values, index) => ({
					jsonKey: 'recommendationExtensionOfStayInfoJson',
					name: 'appcSerialNo',
					repeat: 5,
					'data-tabIndex': index,
					maxHeight: 31,
					maxLength: 3,
					className: 'text-center',
					containerClassName: 'absolute top-[628px] left-[77px] w-[35px]',
					boxStyle: {
						top: `${628 + 32.5 * index}px`,
					},
					regex: REGEX.ALLOW_NUMBER,
				}),
				(values, index) => ({
					jsonKey: 'recommendationExtensionOfStayInfoJson',
					name: 'appcName',
					repeat: 5,
					'data-tabIndex': index,
					maxHeight: 31,
					maxLength: 40,
					containerClassName: 'absolute top-[628px] left-[115px] w-[165px]',
					className: 'leading-[16px]',
					boxStyle: {
						top: `${628 + 32.5 * index}px`,
					},
				}),
				(values, index) => ({
					jsonKey: 'recommendationExtensionOfStayInfoJson',
					name: 'appcAlienRegistNo',
					repeat: 5,
					'data-tabIndex': index,
					maxHeight: 31,
					maxLength: 23,
					containerClassName: 'absolute top-[628px] left-[284px] w-[98px]',
					boxStyle: {
						top: `${628 + 32.5 * index}px`,
					},
				}),
				(values, index) => ({
					jsonKey: 'recommendationExtensionOfStayInfoJson',
					name: 'appcNational',
					repeat: 5,
					'data-tabIndex': index,
					maxHeight: 31,
					maxLength: 10,
					containerClassName:
						'absolute top-[628px] left-[390px] w-[62px] leading-[16px]',
					boxStyle: {
						top: `${628 + 32.5 * index}px`,
					},
				}),
				(values, index) => ({
					jsonKey: 'recommendationExtensionOfStayInfoJson',
					name: 'appcPassportNo',
					repeat: 5,
					'data-tabIndex': index,
					maxHeight: 31,
					maxLength: 15,
					containerClassName:
						'absolute top-[628px] left-[459px] w-[71px] leading-[16px]',
					boxStyle: {
						top: `${628 + 32.5 * index}px`,
					},
				}),
				(data, index) => ({
					jsonKey: 'recommendationExtensionOfStayInfoJson',
					name: 'appcSignature',
					repeat: 5,
					'data-tabIndex': index,
					maxHeight: 31,
					containerClassName: 'absolute left-[607px] w-[106px] h-8',
					className: 'leading-[16px]',
					boxStyle: {
						top: `${628 + 32.5 * index}px`,
					},
				}),
				(values, index) => ({
					name: 'dearToText',
					maxHeight: 20,
					maxLength: 30,
					className: 'text-right focus:!bg-white',
					containerClassName: cn(
						'absolute top-[925px] left-[77px] w-[188px] items-end',
						values.dearToText && '!bg-white'
					),
				}),
				{
					name: 'recomExtOfStayEmployer',
					maxHeight: 24,
					maxLength: 5,
					containerClassName: 'absolute top-[897px] left-[415px] w-[130px]',
					className: 'text-[17.33px] leading-none',
				},
			],
			datePickers: [
				{
					name: 'receiptDate',
					className:
						'[&>.ant-picker-input>input]:!text-[14px] [&>.ant-picker-input>input]:!text-center w-[150px]',
					containerClassName: 'absolute top-[165px] left-[400px]',
					style: {
						fontSize: 14,
						textAlign: 'center',
						width: '150px',
					},
				},
				{
					name: 'processingEndDate',
					className:
						'[&>.ant-picker-input>input]:!text-[14px] [&>.ant-picker-input>input]:!text-center w-[100px]',
					containerClassName: 'absolute top-[165px] left-[609px]',
					style: {
						fontSize: 14,
						textAlign: 'center',
						width: '100px',
					},
				},
				(data, index) => ({
					repeat: 5,
					jsonKey: 'recommendationExtensionOfStayInfoJson',
					name: 'appcStayPeriod',
					className:
						'[&>.ant-picker-input>input]:!text-[12px] [&>.ant-picker-input>input]:!top-[2px] w-[108px]',
					format: 'YYYY-MM-DD',
					style: { fontSize: 12, width: '100px' },
					boxStyle: {
						top: `${628 + 32.5 * index}px`,
					},
					containerClassName: 'absolute left-[536px] w-[68px] h-[31px]',
				}),
				{
					name: 'recomExtOfStayRegistDate',
					segmentedInputs: true,
					style: { fontSize: '14px' },
					format: 'YYYY        MM         DD',
					containerClassName: 'absolute top-[868px] left-[559px]',
				},
			],
			checkboxes: [
				{
					name: 'factStatement',
					iconType: 'circle',
					type: CheckboxType.MULTIPLE,
					options: Object.values(FactType),
					classNames: [
						'absolute top-[370px] left-[228px]',
						'absolute top-[399px] left-[228px]',
						'absolute top-[428px] left-[228px]',
					],
				},
				{
					name: 'reasonsAgriculture',
					iconType: 'circle',
					type: CheckboxType.MULTIPLE,
					options: Object.values(AgricultureReasonType),
					classNames: [
						'absolute top-[463px] left-[322px]',
						'absolute top-[463px] left-[408px]',
						'absolute top-[463px] left-[494px]',
						'absolute top-[463px] left-[614px]',
					],
				},
				{
					name: 'reasonsFishing',
					iconType: 'circle',
					type: CheckboxType.MULTIPLE,
					options: Object.values(FishingReasonType),
					classNames: [
						'absolute top-[493px] left-[322px]',
						'absolute top-[493px] left-[408px]',
					],
				},
				{
					name: 'period',
					iconType: 'circle',
					options: Object.values(ExtensionStayEnum),
					type: CheckboxType.MULTIPLE,
					classNames: [
						'absolute top-[526px] left-[267px]',
						'absolute top-[526px] left-[352px]',
						'absolute top-[526px] left-[437px]',
						'absolute top-[526px] left-[522px]',
						'absolute top-[526px] left-[606px]',
					],
				},
			],
			signatures: [
				{
					name: 'recomExtOfStaySignature',
					className: 'absolute top-[895px] left-[625px] w-[85px] h-[40px]',
				},
			],
			highlightBoxes: [
				// {
				// 	name: 'registrationNo',
				// 	className: 'top-[167px] left-[81px] w-[61px] !h-[18px]',
				// 	ignoreValidate: true,
				// },
				// {
				// 	name: 'receiptDate',
				// 	className: 'top-[167px] left-[365px] w-[45px] !h-[18px]',
				// 	ignoreValidate: true,
				// },
				// {
				// 	name: 'processingEndDate',
				// 	className: 'top-[167px] left-[551px] w-[61px] !h-[18px]',
				// 	ignoreValidate: true,
				// },
				{
					name: 'recruiterName',
					className: 'top-[207px] left-[81px] w-[86px] !h-[18px]',
				},
				{
					name: 'franchisePhone',
					className: 'top-[207px] left-[458px] w-[62px] !h-[18px]',
				},
				{
					name: 'franchiseName',
					className: 'top-[248px] left-[81px] w-[48px] !h-[18px]',
				},
				{
					name: 'franchiseAddress',
					className: 'top-[248px] left-[458px] w-[33px] !h-[18px]',
				},
				(values, index) => ({
					name: 'businessRegisNo',
					className: 'top-[292px] left-[81px] w-[203px] !h-[18px]',
					hasError: values?.businessRegisNo?.length < 13,
				}),
				{
					name: 'franchiseType',
					className: 'top-[292px] left-[458px] w-[152px] !h-[18px]',
				},
				{
					name: 'factStatement',
					className: 'top-[402px] left-[81px] w-[113px] !h-[17px]',
				},
				{
					names: ['reasonsAgriculture', 'reasonsFishing'],
					className: 'top-[476px] left-[211px] w-[39px] !h-[23px]',
				},
				{
					names: ['period'],
					className: 'top-[523px] left-[211px] w-[39px] !h-[23px]',
				},
				{
					path: ['recommendationExtensionOfStayInfoJson', '0', 'appcSerialNo'],
					className: 'top-[601px] left-[81px] w-[34px] !h-[18px]',
				},
				{
					path: ['recommendationExtensionOfStayInfoJson', '0', 'appcName'],
					className: 'top-[601px] left-[184px] w-[35px] !h-[18px]',
				},
				{
					path: [
						'recommendationExtensionOfStayInfoJson',
						'0',
						'appcAlienRegistNo',
					],
					className: 'top-[595px] left-[304px] w-[61px] !h-[32px]',
				},
				{
					path: ['recommendationExtensionOfStayInfoJson', '0', 'appcNational'],
					className: 'top-[601px] left-[405px] w-[35px] !h-[18px]',
				},
				{
					path: [
						'recommendationExtensionOfStayInfoJson',
						'0',
						'appcPassportNo',
					],
					className: 'top-[601px] left-[464px] w-[61px] !h-[18px]',
				},
				{
					path: [
						'recommendationExtensionOfStayInfoJson',
						'0',
						'appcStayPeriod',
					],
					className: 'top-[594px] left-[539px] w-[61px] !h-[32px]',
				},
				{
					path: ['recommendationExtensionOfStayInfoJson', '0', 'appcSignature'],
					className: 'top-[594px] left-[609px] w-[103px] !h-[32px]',
				},
				{
					name: 'recomExtOfStayRegistDate',
					className: 'top-[870px] left-[543px] w-[162px] !h-[18px]',
				},
				{
					name: 'dearToText',
					className: 'top-[925px] left-[115px] w-[188px] !h-[18px]',
				},
				{
					name: 'recomExtOfStayEmployer',
					className: 'top-[900px] left-[362px] w-[44px] !h-[18px]',
				},
				{
					name: 'recomExtOfStaySignature',
					className: 'top-[906px] left-[629px] w-[78px] !h-[20px]',
				},
			],
		},
	],
	validationSchema: Yup.object()
		.shape({
			recruiterName: Yup.string().required(),
			franchiseName: Yup.string().required(),
			franchisePhone: Yup.string().required(),
			franchiseAddress: Yup.string().required(),
			businessRegisNo: Yup.string().required().min(13),
			franchiseType: Yup.string().required(),
			factStatement: Yup.array().of(Yup.string()).required(),
			period: Yup.array().of(Yup.string()).required(),
			recomExtOfStayRegistDate: Yup.string().required(),
			recomExtOfStayEmployer: Yup.string().required(),
			recomExtOfStaySignature: Yup.string().required(),
		})
		.test(
			'at-least-one-field',
			'At least one field must have data',
			function (value) {
				const fields = [['reasonsAgriculture', 'reasonsFishing']];
				return fields.every((field) =>
					// @ts-expect-error: ignore here
					field?.some((f) => isNotNilOrEmpty(value[f]))
				);
			}
		)
		.test(
			'recommendationExtensionOfStayInfoJson',
			'recommendationExtensionOfStayInfoJson',
			function (value) {
				const fields = [
					'appcSerialNo',
					'appcName',
					'appcAlienRegistNo',
					'appcNational',
					'appcPassportNo',
					'appcStayPeriod',
					'appcSignature',
				];

				return fields?.every((field) =>
					isNotNilOrEmpty(
						// @ts-expect-error: ignore here
						value?.recommendationExtensionOfStayInfoJson?.[0]?.[field]
					)
				);
			}
		),
};
